import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/input';
import { Badge } from '~/components/ui/badge';
import { streamCoachTurn, type CoachStateEvent } from '~/lib/coach-stream';

export const Route = createFileRoute('/_authed/coach')({
  component: Coach,
});

interface Turn {
  id: string;
  user: string;
  assistant: string;
  state: CoachStateEvent | null;
  done: boolean;
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

function Coach() {
  const nav = useNavigate();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns]);

  async function send() {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    const id = makeId();
    setTurns((prev) => [...prev, { id, user: text, assistant: '', state: null, done: false }]);
    setStreaming(true);
    try {
      for await (const evt of streamCoachTurn(text)) {
        if (evt.type === 'crisis') {
          nav({ to: '/crisis-resources' });
          return;
        }
        if (evt.type === 'state') {
          setTurns((prev) =>
            prev.map((t) => (t.id === id ? { ...t, state: evt.data } : t)),
          );
        } else if (evt.type === 'token') {
          setTurns((prev) =>
            prev.map((t) =>
              t.id === id ? { ...t, assistant: t.assistant + evt.data } : t,
            ),
          );
        } else if (evt.type === 'done') {
          setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, done: true } : t)));
        } else if (evt.type === 'error') {
          setTurns((prev) =>
            prev.map((t) =>
              t.id === id
                ? { ...t, assistant: t.assistant + `\n\n(error: ${evt.data.message})`, done: true }
                : t,
            ),
          );
        }
      }
    } finally {
      setStreaming(false);
    }
  }

  return (
    <section className="mx-auto max-w-2xl px-4 sm:px-6 py-8 flex flex-col h-[calc(100vh-3.5rem)] animate-rise-soft">
      <header className="shrink-0">
        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
          coach
        </div>
        <h1 className="font-serif text-2xl leading-tight">
          What&apos;s on your mind?
        </h1>
      </header>

      <div className="flex-1 overflow-y-auto mt-6 -mx-2 px-2 space-y-8">
        {turns.length === 0 && (
          <div className="rounded-[var(--radius-lg)] border border-dashed border-border bg-muted/30 p-6">
            <p className="text-sm text-muted-foreground leading-relaxed max-w-prose">
              Say what&apos;s actually true right now. One sentence is enough.
              The coach will be honest with you and won&apos;t end every reply
              with a question.
            </p>
          </div>
        )}
        {turns.map((t) => (
          <div key={t.id} className="space-y-5 animate-rise">
            {/* user — right-aligned, subtle background */}
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-[var(--radius)] bg-muted text-foreground px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap">
                {t.user}
              </div>
            </div>

            {/* intervention card — kicker + title + state */}
            {t.state?.intervention && (
              <div className="rounded-[var(--radius)] border border-[var(--color-accent-soft)] bg-[var(--color-accent-soft)]/40 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-accent)]/80">
                      {t.state.intervention.kind.replace(/_/g, ' ')}
                    </div>
                    <div className="font-serif text-[15px] leading-tight mt-0.5">
                      {t.state.intervention.title}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    <Badge variant="outline">{t.state.load}</Badge>
                    {t.state.hrvStrain && <Badge variant="accent">hrv strain</Badge>}
                    {t.state.timeFrameTriggered && (
                      <Badge variant="outline">time pressure</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* AI — flush left, no bubble, reads like prose */}
            <div className="text-[16px] leading-[1.65] whitespace-pre-wrap">
              {t.assistant || (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <span className="inline-block size-1.5 rounded-full bg-muted-foreground/60 animate-pulse" />
                  <span className="inline-block size-1.5 rounded-full bg-muted-foreground/40 animate-pulse [animation-delay:120ms]" />
                  <span className="inline-block size-1.5 rounded-full bg-muted-foreground/20 animate-pulse [animation-delay:240ms]" />
                </span>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form
        className="mt-4 border-t border-border pt-4 shrink-0"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <div className="flex gap-2 items-end">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="what's true right now?"
            disabled={streaming}
          />
          <Button type="submit" disabled={streaming || !input.trim()} size="lg">
            {streaming ? '…' : 'Send'}
          </Button>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
          <span>⌘/ctrl + enter to send</span>
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            onClick={() => nav({ to: '/crisis-resources' })}
          >
            crisis resources
          </button>
        </div>
      </form>
    </section>
  );
}
